import { useHandler } from "@redotech/react-util/hook";
import BoxIcon from "@redotech/redo-icons/box-01.svg";
import Fuse from "fuse.js";
import { JSXElementConstructor, useMemo } from "react";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { NavItem } from "../arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { HydratedTreeNode, useTreeContext } from "../tree/tree-context";
import { CommandPaletteItem } from "./contexts/command-palette-context";

interface MappedNavItem {
  path: string[];
  Icon: JSXElementConstructor<any>;
  url: string;
  joinedPath: string;
}

const DefaultIcon = BoxIcon;

/**
 * Client-side fuzzy search over the registered navigation tree. Returns command
 * palette items that navigate to a matched page. Any app that wraps its shell in
 * a `TreeProvider` and registers `RedoSecondaryNavItem`s (via `useNavTreeNode`)
 * gets page search for free.
 */
export function usePageSearch(): (
  search: string,
  max: number,
) => CommandPaletteItem[] {
  const { trees } = useTreeContext<NavItem>();

  const navigate = useNavigate();

  const navItems = useMemo(() => trees.flatMap(mapTreeToNavItems), [trees]);

  const searcher = useMemo(() => {
    return new Fuse<MappedNavItem>(navItems, {
      keys: [{ name: "joinedPath" }],
      includeScore: true,
      isCaseSensitive: false,
      ignoreLocation: true,
      useExtendedSearch: true,
      minMatchCharLength: 1,
      threshold: 0.1,
      findAllMatches: true,
      shouldSort: true,
    });
  }, [navItems]);

  return useHandler((search: string, max: number): CommandPaletteItem[] => {
    if (!search.trim()) {
      return [];
    }

    // Split search into words and create an extended search pattern
    // that requires all words to be present (using AND logic with spaces)
    const searchWords = search.trim().split(/\s+/);

    // If single word, do normal search
    if (searchWords.length === 1) {
      const results = searcher.search(search);
      const mapped = results.map((result) =>
        mappedNavItemToCommandPaletteItem(result.item, navigate),
      );
      return mapped.slice(0, max);
    }

    // For multiple words, create an extended search pattern that matches all words
    // Using space-separated terms for AND logic (all terms must be present)
    const extendedSearchPattern = searchWords.join(" ");
    const results = searcher.search(extendedSearchPattern);

    const mappedResults = results.map((result) =>
      mappedNavItemToCommandPaletteItem(result.item, navigate),
    );
    return mappedResults.slice(0, max);
  });
}

function mapTreeToNavItems(root: HydratedTreeNode<NavItem>): MappedNavItem[] {
  const results: MappedNavItem[] = [];

  function traverse(
    node: HydratedTreeNode<NavItem>,
    parentPath: string[] = [],
    lastIcon?: JSXElementConstructor<any>,
  ): void {
    const currentPath = [...parentPath, node.data.friendlyName];
    const currentIcon = node.data.Icon || lastIcon || DefaultIcon;

    // If this is a leaf node (no children) and has a URL, add it to results
    if (node.children.length === 0) {
      if (node.data.url) {
        results.push({
          path: currentPath,
          Icon: currentIcon,
          url: node.data.url,
          joinedPath: currentPath.join(" "),
        });
      }
    } else {
      // Traverse children, passing down the current icon if defined
      for (const child of node.children) {
        traverse(child, currentPath, currentIcon);
      }
    }
  }

  traverse(root);
  return results;
}

function mappedNavItemToCommandPaletteItem(
  item: MappedNavItem,
  navigate: NavigateFunction,
): CommandPaletteItem {
  return {
    id: item.url,
    title: item.path.join(" > "),
    category: "pages",
    group: "Pages",
    icon: <item.Icon />,
    action: ({ newTab }) => {
      if (newTab) {
        window.open(item.url, "_blank");
      } else {
        navigate(item.url);
      }
    },
  };
}
